import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, AlertCircle, XCircle, X, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { alertCards } from "@/data/mockDashboard";
import type { AlertCard } from "@/data/mockDashboard";
import { useDashboardAlerts } from "@/hooks/use-dashboard";
import { toAlertCards } from "@/api/dashboard";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { REVEAL_ON_HOVER } from "@/lib/touchReveal";
import { bellLabel, type BellStatus } from "@/lib/bellStatus";

const SEVERITY_CONFIG = {
  destructive: {
    icon: XCircle,
    bg: "bg-destructive/10",
    text: "text-destructive",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-warning/10",
    text: "text-warning",
  },
  info: {
    icon: AlertCircle,
    bg: "bg-info/10",
    text: "text-info",
  },
} as const;

/** FP-060: the stale-connection alert asks for a reconnect and names no place, so
 *  the row carries the way there. The page is Connected Banks (`/banks`). */
export function AlertAction({ alert, onNavigate }: { alert: AlertCard; onNavigate: () => void }) {
  if (alert.type !== "stale-connection") return null;
  return (
    <Link
      to="/banks"
      onClick={onNavigate}
      className="tap-target mt-1 inline-flex items-center text-xs font-medium text-foreground underline underline-offset-2"
    >
      Reconnect in Connected Banks
    </Link>
  );
}

function SeverityIcon({ severity }: { severity: AlertCard["severity"] }) {
  const config = SEVERITY_CONFIG[severity];
  const Icon = config.icon;
  return (
    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${config.bg}`}>
      <Icon className={`h-3.5 w-3.5 ${config.text}`} />
    </span>
  );
}

/** What the popover shows when there is no list to show (NOT-007). */
export function BellEmptyState({ status, onRetry }: { status: BellStatus; onRetry: () => void }) {
  if (status === "loading") {
    return (
      <div role="status" className="py-12 text-center text-sm text-muted-foreground">
        Loading notifications…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm font-medium text-foreground">Couldn&rsquo;t load notifications</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Bell className="h-5 w-5 text-muted-foreground/50" />
      </span>
      <div>
        <p className="text-sm font-medium text-muted-foreground">All caught up</p>
        <p className="mt-0.5 text-xs text-muted-foreground">No new notifications</p>
      </div>
    </div>
  );
}

export function NotificationBell({ dark = false }: { dark?: boolean }) {
  const [open, setOpen] = useState(false);
  // Track dismissals by id (not by replacing the list) so async real-API data
  // can stream in without resurrecting already-dismissed alerts.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const alertsQ = useDashboardAlerts();
  const sourceAlerts: AlertCard[] = FEATURE_FLAGS.DASHBOARD_REAL_API
    ? toAlertCards(alertsQ.data)
    : alertCards;
  const alerts = sourceAlerts.filter((a) => !dismissedIds.has(a.id));
  // NOT-007: loading and failure are not "nothing to report". An empty list is
  // only "All caught up" once the alerts have actually arrived.
  const status: BellStatus = !FEATURE_FLAGS.DASHBOARD_REAL_API
    ? "ready"
    : alertsQ.isError && !alertsQ.data
      ? "error"
      : alertsQ.isPending
        ? "loading"
        : "ready";

  const count = status === "ready" ? alerts.length : 0;

  const dismiss = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  const dismissAll = () => {
    setDismissedIds(new Set(sourceAlerts.map((a) => a.id)));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={bellLabel(status, count)}
          className={
            dark
              ? "relative rounded-lg border border-[var(--forecast-card-border)] bg-[var(--forecast-card)] p-2 text-[var(--forecast-text-muted)] transition-colors hover:text-white"
              : "relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          }
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white"
            >
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[340px] overflow-hidden rounded-xl p-0 shadow-lg"
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-muted/50 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            {count > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground/10 px-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                {count}
              </span>
            )}
          </div>
          {alerts.length > 0 && (
            <button
              type="button"
              onClick={dismissAll}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <CheckCheck className="h-3 w-3" />
              Clear all
            </button>
          )}
        </div>

        {/* Alert list */}
        <div className="max-h-[380px] overflow-y-auto">
          {status !== "ready" || alerts.length === 0 ? (
            <BellEmptyState status={status} onRetry={() => void alertsQ.refetch()} />
          ) : (
            <ul role="list" className="divide-y divide-border/50">
              {alerts.map((alert) => (
                <li
                  key={alert.id}
                  role="listitem"
                  className="group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/30"
                >
                  <SeverityIcon severity={alert.severity} />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-[13px] font-semibold leading-snug">{alert.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {alert.message}
                    </p>
                    <AlertAction alert={alert} onNavigate={() => setOpen(false)} />
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(alert.id)}
                    aria-label={`Dismiss ${alert.title}`}
                    className={`mt-1 shrink-0 rounded-md p-1 text-muted-foreground/50 transition-all hover:bg-accent hover:text-foreground ${REVEAL_ON_HOVER}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
